/**
 * DEV-ONLY admin dashboard at /admin/ux.
 * Guarded: renders NotFound in production builds.
 * Never sends UX tracking events.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import NotFound from "@/pages/not-found";

// ─── Types ────────────────────────────────────────────────────────────────────

interface MetricsGroup {
  key?: string;
  goalId: string;
  ctaVariant: string;
  homeVariant: string;
  source: string;
  starts: number;
  completes: number;
  conversionRate: number;
}

interface ExtendedMetricsGroup {
  key?: string;
  goalId: string;
  vb_cta: string;
  vb_home: string;
  vb_services: string;
  vb_faq: string;
  vb_intent: string;
  vb_source: string;
  starts: number;
  completes: number;
  conversionRate: number;
}

interface MetricsResponse {
  updatedAt: number;
  groups: MetricsGroup[];
  groupsBasic: MetricsGroup[];
  groupsExtended: ExtendedMetricsGroup[];
  totals: {
    byGoal: Record<string, { starts: number; completes: number; conversionRate: number }>;
  };
}

interface SessionDebugData {
  snapshot: unknown;
  policy: unknown;
  predict: unknown;
  snapshotStatus: number;
  policyStatus: number;
  predictStatus: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function relativeTime(ts: number): string {
  const sec = Math.round((Date.now() - ts) / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  return `${Math.round(sec / 3600)}h ago`;
}

function sourceBadge(source: string) {
  const color =
    source === "openclaw"
      ? "bg-green-900/30 text-green-400 border-green-800/40"
      : source === "deterministic"
      ? "bg-slate-800/50 text-slate-400 border-slate-700/40"
      : "bg-yellow-900/30 text-yellow-400 border-yellow-800/40";
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium border ${color}`}>
      {source}
    </span>
  );
}

function rateColor(rate: number): string {
  if (rate >= 0.6) return "text-green-400";
  if (rate >= 0.3) return "text-yellow-400";
  return "text-red-400";
}

async function safeFetch(url: string): Promise<{ status: number; data: unknown }> {
  try {
    const r = await fetch(url, { credentials: "same-origin" });
    if (r.status === 204 || r.status === 404) return { status: r.status, data: null };
    const data = await r.json().catch(() => null);
    return { status: r.status, data };
  } catch {
    return { status: 0, data: null };
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function JsonCard({ title, status, data }: { title: string; status: number; data: unknown }) {
  const statusColor =
    status === 200 ? "text-green-400" : status === 204 ? "text-slate-400" : "text-red-400";
  return (
    <Card className="bg-card/50 border-border/30">
      <CardHeader className="py-3 px-4 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </CardTitle>
        <span className={`text-xs font-mono ${statusColor}`}>HTTP {status || "ERR"}</span>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {data ? (
          <pre className="text-xs text-foreground/80 overflow-auto max-h-64 rounded bg-black/30 p-3 leading-relaxed">
            {JSON.stringify(data, null, 2)}
          </pre>
        ) : (
          <p className="text-xs text-muted-foreground italic">No data (empty or not found)</p>
        )}
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminUx() {
  // Production guard
  if (import.meta.env.PROD) return <NotFound />;

  const [, navigate] = useLocation();

  // Metrics state
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  // Leaderboard filters + mode
  const [filterGoal, setFilterGoal] = useState<string>("all");
  const [filterSource, setFilterSource] = useState<string>("all");
  const [leaderboardMode, setLeaderboardMode] = useState<"basic" | "extended">("basic");

  // Session debug state
  const [sessionInput, setSessionInput] = useState("");
  const [sessionData, setSessionData] = useState<SessionDebugData | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const { status, data } = await safeFetch("/api/ux-metrics");
      if (status === 200 && data) {
        setMetrics(data as MetricsResponse);
        setLastUpdated(Date.now());
        setMetricsError(null);
      } else if (status === 204) {
        setMetricsError("ADAPTIVE_UX_ENABLED is off — enable it to see metrics.");
      } else if (status === 404) {
        setMetricsError("Metrics endpoint not available (production build or wrong NODE_ENV).");
      } else {
        setMetricsError(`Unexpected response: HTTP ${status}`);
      }
    } catch {
      setMetricsError("Failed to fetch metrics.");
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  // Initial fetch + polling (5s, visibility-aware)
  useEffect(() => {
    void fetchMetrics();

    pollingRef.current = setInterval(() => {
      if (document.visibilityState === "visible") void fetchMetrics();
    }, 5000);

    const onVisibility = () => {
      if (document.visibilityState === "visible") void fetchMetrics();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchMetrics]);

  const fetchSession = async () => {
    const sid = sessionInput.trim();
    if (!sid) return;
    setSessionLoading(true);
    setSessionError(null);
    setSessionData(null);
    try {
      const [snap, pol, pred] = await Promise.all([
        safeFetch(`/api/ux-snapshot?session_id=${encodeURIComponent(sid)}`),
        safeFetch(`/api/ux-policy?session_id=${encodeURIComponent(sid)}`),
        safeFetch(`/api/ux-predict?session_id=${encodeURIComponent(sid)}`),
      ]);
      setSessionData({
        snapshot: snap.data,
        snapshotStatus: snap.status,
        policy: pol.data,
        policyStatus: pol.status,
        predict: pred.data,
        predictStatus: pred.status,
      });
    } catch {
      setSessionError("Fetch failed.");
    } finally {
      setSessionLoading(false);
    }
  };

  // Derived data
  const basicGroups = metrics?.groupsBasic ?? metrics?.groups ?? [];
  const extGroups = metrics?.groupsExtended ?? [];

  const allGoals = metrics
    ? ["all", ...Array.from(new Set(basicGroups.map((g) => g.goalId))).sort()]
    : ["all"];
  const allSources = metrics
    ? ["all", ...Array.from(new Set(basicGroups.map((g) => g.source))).sort()]
    : ["all"];

  const filteredBasic = basicGroups
    .filter((g) => filterGoal === "all" || g.goalId === filterGoal)
    .filter((g) => filterSource === "all" || g.source === filterSource)
    .sort((a, b) => b.conversionRate - a.conversionRate || b.completes - a.completes)
    .slice(0, 20);

  const filteredExtended = extGroups
    .filter((g) => filterGoal === "all" || g.goalId === filterGoal)
    .filter((g) => filterSource === "all" || g.vb_source === filterSource)
    .sort((a, b) => b.conversionRate - a.conversionRate || b.completes - a.completes)
    .slice(0, 20);

  const filteredGroups = leaderboardMode === "extended" ? filteredExtended : filteredBasic;

  const nudgeGroups = basicGroups.filter((g) =>
    g.goalId.startsWith("nudge") || g.goalId.startsWith("goal_whatsapp") || g.goalId.startsWith("goal_access"),
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border/30 bg-card/20 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs px-2 py-0.5 rounded bg-amber-900/40 text-amber-400 border border-amber-700/30">
              DEV
            </span>
            <h1 className="text-sm font-semibold text-foreground">
              Adaptive UX Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {lastUpdated && (
              <span className="text-xs text-muted-foreground">
                Updated {relativeTime(lastUpdated)}
              </span>
            )}
            <Button size="sm" variant="outline" onClick={() => void fetchMetrics()} className="h-7 text-xs">
              Refresh
            </Button>
            <Button size="sm" variant="ghost" onClick={() => navigate("/")} className="h-7 text-xs text-muted-foreground">
              ← Back to site
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {metricsLoading ? (
          <LoadingSkeleton />
        ) : metricsError ? (
          <Card className="border-destructive/30 bg-destructive/5">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-destructive">{metricsError}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Make sure <code className="bg-black/20 px-1 rounded">ADAPTIVE_UX_ENABLED=1</code> and{" "}
                <code className="bg-black/20 px-1 rounded">NODE_ENV=development</code>.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="overview">
            <TabsList className="mb-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
              <TabsTrigger value="nudges">Nudges</TabsTrigger>
              <TabsTrigger value="session">Session Debug</TabsTrigger>
            </TabsList>

            {/* ── A) Overview: KPIs by goal ──────────────────────────────── */}
            <TabsContent value="overview">
              <div className="space-y-6">
                {/* KPI cards */}
                {metrics && Object.entries(metrics.totals.byGoal).length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.entries(metrics.totals.byGoal)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([goal, kpi]) => (
                        <Card key={goal} className="bg-card/50 border-border/30">
                          <CardContent className="pt-4 pb-4 px-4">
                            <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider mb-1 truncate">
                              {goal}
                            </p>
                            <p className={`text-2xl font-bold tabular-nums ${rateColor(kpi.conversionRate)}`}>
                              {pct(kpi.conversionRate)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {kpi.completes}/{kpi.starts} completed
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No goal data yet. Send some goal events to see KPIs here.</p>
                )}

                {/* Totals table */}
                {metrics && Object.entries(metrics.totals.byGoal).length > 0 && (
                  <Card className="bg-card/50 border-border/30">
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-sm">Goal KPIs</CardTitle>
                    </CardHeader>
                    <CardContent className="px-0 pb-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="pl-4">Goal</TableHead>
                            <TableHead className="text-right">Starts</TableHead>
                            <TableHead className="text-right">Completes</TableHead>
                            <TableHead className="text-right pr-4">Conversion</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(metrics.totals.byGoal)
                            .sort(([, a], [, b]) => b.starts - a.starts)
                            .map(([goal, kpi]) => (
                              <TableRow key={goal}>
                                <TableCell className="pl-4 font-mono text-xs">{goal}</TableCell>
                                <TableCell className="text-right tabular-nums">{kpi.starts}</TableCell>
                                <TableCell className="text-right tabular-nums">{kpi.completes}</TableCell>
                                <TableCell className={`text-right tabular-nums font-semibold pr-4 ${rateColor(kpi.conversionRate)}`}>
                                  {pct(kpi.conversionRate)}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* ── B) Leaderboard ─────────────────────────────────────────── */}
            <TabsContent value="leaderboard">
              <div className="space-y-4">
                {/* Filters + mode toggle */}
                <div className="flex gap-3 flex-wrap items-center">
                  {/* Basic / Extended toggle */}
                  <div className="flex rounded-md border border-border/40 overflow-hidden text-xs h-8">
                    <button
                      className={`px-3 h-full transition-colors ${leaderboardMode === "basic" ? "bg-primary/15 text-primary font-medium" : "text-muted-foreground hover:bg-muted/50"}`}
                      onClick={() => setLeaderboardMode("basic")}
                    >
                      Basic
                    </button>
                    <button
                      className={`px-3 h-full border-l border-border/40 transition-colors ${leaderboardMode === "extended" ? "bg-primary/15 text-primary font-medium" : "text-muted-foreground hover:bg-muted/50"}`}
                      onClick={() => setLeaderboardMode("extended")}
                    >
                      Extended
                      {extGroups.length > 0 && (
                        <span className="ml-1 rounded bg-green-900/40 text-green-400 px-1">{extGroups.length}</span>
                      )}
                    </button>
                  </div>

                  <Select value={filterGoal} onValueChange={setFilterGoal}>
                    <SelectTrigger className="h-8 w-44 text-xs">
                      <SelectValue placeholder="Filter by goal" />
                    </SelectTrigger>
                    <SelectContent>
                      {allGoals.map((g) => (
                        <SelectItem key={g} value={g} className="text-xs">
                          {g === "all" ? "All goals" : g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterSource} onValueChange={setFilterSource}>
                    <SelectTrigger className="h-8 w-44 text-xs">
                      <SelectValue placeholder="Filter by source" />
                    </SelectTrigger>
                    <SelectContent>
                      {allSources.map((s) => (
                        <SelectItem key={s} value={s} className="text-xs">
                          {s === "all" ? "All sources" : s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(filterGoal !== "all" || filterSource !== "all") && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={() => { setFilterGoal("all"); setFilterSource("all"); }}
                    >
                      Clear
                    </Button>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {filteredGroups.length} groups (top 20)
                  </span>
                </div>

                {leaderboardMode === "extended" && extGroups.length === 0 && (
                  <div className="rounded-lg border border-amber-700/30 bg-amber-900/10 px-4 py-3 text-xs text-amber-400">
                    No extended groups yet. Extended data requires goal events with <code className="bg-black/20 px-1 rounded">vb_*</code> meta (sent automatically when tracking is active and adaptive variants are loaded on the page).
                  </div>
                )}

                {/* Table */}
                <Card className="bg-card/50 border-border/30">
                  <CardContent className="px-0 pb-0">
                    {filteredGroups.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-muted-foreground">No groups match the current filters.</p>
                    ) : leaderboardMode === "basic" ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="pl-4">#</TableHead>
                            <TableHead>Goal</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead>CTA Variant</TableHead>
                            <TableHead>Home Variant</TableHead>
                            <TableHead className="text-right">Starts</TableHead>
                            <TableHead className="text-right">Completes</TableHead>
                            <TableHead className="text-right pr-4">Conv. Rate</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(filteredGroups as MetricsGroup[]).map((g, i) => (
                            <TableRow key={g.key ?? `${g.goalId}-${g.source}-${g.ctaVariant}-${g.homeVariant}-${i}`}>
                              <TableCell className="pl-4 text-muted-foreground tabular-nums">{i + 1}</TableCell>
                              <TableCell className="font-mono text-xs">{g.goalId}</TableCell>
                              <TableCell>{sourceBadge(g.source)}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{g.ctaVariant}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{g.homeVariant}</TableCell>
                              <TableCell className="text-right tabular-nums">{g.starts}</TableCell>
                              <TableCell className="text-right tabular-nums">{g.completes}</TableCell>
                              <TableCell className={`text-right tabular-nums font-semibold pr-4 ${rateColor(g.conversionRate)}`}>
                                {pct(g.conversionRate)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      /* Extended table */
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="pl-4">#</TableHead>
                              <TableHead>Goal</TableHead>
                              <TableHead>CTA</TableHead>
                              <TableHead>Home</TableHead>
                              <TableHead>Services</TableHead>
                              <TableHead>FAQ</TableHead>
                              <TableHead>Intent</TableHead>
                              <TableHead>Source</TableHead>
                              <TableHead className="text-right">Starts</TableHead>
                              <TableHead className="text-right">Completes</TableHead>
                              <TableHead className="text-right pr-4">Conv.</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(filteredGroups as ExtendedMetricsGroup[]).map((g, i) => (
                              <TableRow key={g.key ?? `ext-${i}`}>
                                <TableCell className="pl-4 text-muted-foreground tabular-nums">{i + 1}</TableCell>
                                <TableCell className="font-mono text-xs">{g.goalId}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{g.vb_cta}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{g.vb_home}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{g.vb_services}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{g.vb_faq}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{g.vb_intent}</TableCell>
                                <TableCell>{sourceBadge(g.vb_source)}</TableCell>
                                <TableCell className="text-right tabular-nums">{g.starts}</TableCell>
                                <TableCell className="text-right tabular-nums">{g.completes}</TableCell>
                                <TableCell className={`text-right tabular-nums font-semibold pr-4 ${rateColor(g.conversionRate)}`}>
                                  {pct(g.conversionRate)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ── C) Nudges summary ──────────────────────────────────────── */}
            <TabsContent value="nudges">
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  Nudge events tracked via <code className="bg-black/20 px-1 rounded">nudge_shown</code> /{" "}
                  <code className="bg-black/20 px-1 rounded">nudge_click</code>. CTR = goal_complete / goal_start.
                </p>
                {nudgeGroups.length === 0 ? (
                  <Card className="bg-card/50 border-border/30">
                    <CardContent className="pt-6 pb-6 text-center">
                      <p className="text-sm text-muted-foreground">No nudge-related goal data yet.</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Enable <code className="bg-black/20 px-1 rounded">UX_NUDGES_ENABLED=1</code> and{" "}
                        <code className="bg-black/20 px-1 rounded">UX_PREDICT_ENABLED=1</code>, then interact with the site.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-card/50 border-border/30">
                    <CardHeader className="py-3 px-4">
                      <CardTitle className="text-sm">Nudge-triggered goals</CardTitle>
                    </CardHeader>
                    <CardContent className="px-0 pb-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="pl-4">Goal</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead className="text-right">Starts</TableHead>
                            <TableHead className="text-right">Completes</TableHead>
                            <TableHead className="text-right pr-4">CTR</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nudgeGroups.map((g) => (
                            <TableRow key={`${g.goalId}-${g.source}`}>
                              <TableCell className="pl-4 font-mono text-xs">{g.goalId}</TableCell>
                              <TableCell>{sourceBadge(g.source)}</TableCell>
                              <TableCell className="text-right tabular-nums">{g.starts}</TableCell>
                              <TableCell className="text-right tabular-nums">{g.completes}</TableCell>
                              <TableCell className={`text-right tabular-nums font-semibold pr-4 ${rateColor(g.conversionRate)}`}>
                                {pct(g.conversionRate)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* ── D) Session debug ───────────────────────────────────────── */}
            <TabsContent value="session">
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={sessionInput}
                    onChange={(e) => setSessionInput(e.target.value)}
                    placeholder="Paste session_id (UUID)…"
                    className="font-mono text-xs h-9 max-w-sm"
                    onKeyDown={(e) => { if (e.key === "Enter") void fetchSession(); }}
                  />
                  <Button
                    size="sm"
                    onClick={() => void fetchSession()}
                    disabled={!sessionInput.trim() || sessionLoading}
                    className="h-9"
                  >
                    {sessionLoading ? "Loading…" : "Fetch"}
                  </Button>
                  {sessionInput && (
                    <Button size="sm" variant="ghost" className="h-9" onClick={() => {
                      const sid = typeof sessionStorage !== "undefined"
                        ? sessionStorage.getItem("ux_session_id")
                        : null;
                      if (sid) setSessionInput(sid);
                    }}>
                      Use current session
                    </Button>
                  )}
                </div>

                {/* "Use current session" helper when input is empty */}
                {!sessionInput && (
                  <button
                    className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                    onClick={() => {
                      const sid = typeof sessionStorage !== "undefined"
                        ? sessionStorage.getItem("ux_session_id")
                        : null;
                      if (sid) setSessionInput(sid);
                      else alert("No ux_session_id in sessionStorage yet. Visit the home page first.");
                    }}
                  >
                    Use current browser session
                  </button>
                )}

                {sessionError && (
                  <p className="text-xs text-destructive">{sessionError}</p>
                )}

                {sessionData && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <JsonCard
                      title="Snapshot /api/ux-snapshot"
                      status={sessionData.snapshotStatus}
                      data={sessionData.snapshot}
                    />
                    <JsonCard
                      title="Policy /api/ux-policy"
                      status={sessionData.policyStatus}
                      data={sessionData.policy}
                    />
                    <JsonCard
                      title="Predict /api/ux-predict"
                      status={sessionData.predictStatus}
                      data={sessionData.predict}
                    />
                  </div>
                )}

                {!sessionData && !sessionLoading && (
                  <p className="text-xs text-muted-foreground">
                    Enter a session ID to inspect the full UX state for that session.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
